import { useEffect, useState } from 'react';
import { ReturnConsignmentsListStore } from 'zustand-store';
import { exchangeAndReturnService } from 'services';
import { useAuth } from 'hooks';
import { returnDetailsFactory } from 'factories';
import { ExchangeChannel, ReturnRequestAction } from 'types';
import { useReturnDetails } from 'pages/exchange-and-return/hooks';
import { Accordion, Button, Loading, Text } from 'components';
import { FontSize, FontWeight } from 'commons';
import * as S from './styles';
import { EntrySummary } from '../components';

export function StepCreated() {
  const { user } = useAuth();
  const { isAdmin } = user;

  const { returnDetails, mutateReturnDetails, validatingReturnDetails } =
    useReturnDetails();
  const { mutate: mutateReturnConsignmentList } =
    ReturnConsignmentsListStore.useGetReturnConsignments();
  const { protocol, entries, option, exchangeChannel } =
    returnDetails || returnDetailsFactory();
  const isPostOffice = option.type === 'POST_OFFICE';
  const isCreatedOnWeb =
    exchangeChannel === ExchangeChannel.web ||
    exchangeChannel === ExchangeChannel.webmobile;
  const [firstTry, setFirstTry] = useState(true);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (isCreatedOnWeb) {
      setFirstTry(false);
      return;
    }

    async function firstTryConfirm() {
      const isConfirmed = await confirmReceipt();

      if (!isConfirmed) {
        setFirstTry(false);
      }
    }

    if (firstTry) {
      firstTryConfirm();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firstTry]);

  async function confirmReceipt() {
    let result: any;

    setIsLoading(true);

    if (isPostOffice) {
      result = await exchangeAndReturnService.postPostCode({
        protocol,
      });
    } else {
      result = await exchangeAndReturnService.postConfirmReceipt({
        protocol,
        action: ReturnRequestAction.Received,
      });
    }

    if (result) {
      mutateReturnDetails();

      mutateReturnConsignmentList();

      return true;
    } else {
      setIsLoading(false);
    }

    return false;
  }

  function renderErrorMessage() {
    if (isCreatedOnWeb) return;

    return isPostOffice ? (
      <Text size={FontSize.font12} className="error">
        <b>Erro ao gerar código de postagem!</b> Tente gerar o código novamente
        ou entre em contato com o time Easy para verificar o problema.
      </Text>
    ) : (
      <Text size={FontSize.font12} className="error">
        <b>Houve um erro em sua solicitação de devolução!</b> Confirme novamente
        para seguir com a devolução
      </Text>
    );
  }

  if (firstTry) {
    return (
      <S.LoaderWrapper>
        <Text size={FontSize.font16} weight={FontWeight.bold}>
          Confirmando devolução
        </Text>

        <Loading small />
      </S.LoaderWrapper>
    );
  }

  return (
    <Accordion title="Solicitação de devolução" defaultOpen>
      <S.Main>
        {renderErrorMessage()}
        {entries.map((summaryEntry, index) => (
          <EntrySummary key={index} summaryEntry={summaryEntry} />
        ))}

        <S.PostWrapper>
          {isPostOffice && (
            <S.PostCodeWrapper>
              <Text size={FontSize.font12}>Código de postagem:</Text>

              <Text weight={FontWeight.bold} className="error">
                Não foi possível gerar o código
              </Text>
            </S.PostCodeWrapper>
          )}

          <S.ButtonWrapper>
            {isPostOffice ? (
              <Button
                full
                onClick={confirmReceipt}
                isLoading={isLoading || validatingReturnDetails}
                disabled={isAdmin || isCreatedOnWeb}
              >
                Gerar código novamente
              </Button>
            ) : (
              <Button
                onClick={confirmReceipt}
                isLoading={isLoading || validatingReturnDetails}
                disabled={isAdmin || isCreatedOnWeb}
              >
                Confirmar devolução
              </Button>
            )}
          </S.ButtonWrapper>
        </S.PostWrapper>
      </S.Main>
    </Accordion>
  );
}
